// 忽略 ESLint 检查规则 @typescript-eslint/no-explicit-any

import { message } from 'antd';
import { AxiosError, isAxiosError } from 'axios';
import Cookies from 'js-cookie';

/**
 * API 错误类型定义
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * 标准化的错误响应接口
 */
export interface ApiError {
  type: ErrorType;
  statusCode?: number;
  message: string;
  details?: unknown;
  originalError?: unknown;
}

/**
 * 错误消息映射
 */
const ERROR_MESSAGES: Record<ErrorType, string> = {
  [ErrorType.NETWORK_ERROR]: '网络连接失败，请检查您的网络',
  [ErrorType.UNAUTHORIZED]: '登录已过期，请重新登录',
  [ErrorType.FORBIDDEN]: '您没有权限执行此操作',
  [ErrorType.NOT_FOUND]: '请求的资源不存在',
  [ErrorType.VALIDATION_ERROR]: '请求参数错误，请检查输入',
  [ErrorType.SERVER_ERROR]: '服务器错误，请稍后重试',
  [ErrorType.TIMEOUT_ERROR]: '请求超时，请重试',
  [ErrorType.UNKNOWN_ERROR]: '未知错误，请联系技术支持',
};

/**
 * 根据 HTTP 状态码判断错误类型
 */
export const getErrorType = (statusCode?: number): ErrorType => {
  if (!statusCode) return ErrorType.NETWORK_ERROR;

  if (statusCode === 401) return ErrorType.UNAUTHORIZED;
  if (statusCode === 403) return ErrorType.FORBIDDEN;
  if (statusCode === 404) return ErrorType.NOT_FOUND;
  if (statusCode >= 400 && statusCode < 500) return ErrorType.VALIDATION_ERROR;
  if (statusCode >= 500) return ErrorType.SERVER_ERROR;

  return ErrorType.UNKNOWN_ERROR;
};

/**
 * 解析 Axios 错误
 */
export const parseAxiosError = (error: AxiosError): ApiError => {
  // 网络错误（无响应）
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return {
        type: ErrorType.TIMEOUT_ERROR,
        message: ERROR_MESSAGES[ErrorType.TIMEOUT_ERROR],
        originalError: error,
      };
    }

    return {
      type: ErrorType.NETWORK_ERROR,
      message: ERROR_MESSAGES[ErrorType.NETWORK_ERROR],
      originalError: error,
    };
  }

  // 有响应的错误
  const statusCode = error.response.status;
  const errorType = getErrorType(statusCode);
  const responseData = error.response.data as {
    message?: string;
    details?: unknown;
    error?: unknown;
  };

  return {
    type: errorType,
    statusCode,
    message: responseData?.message || ERROR_MESSAGES[errorType],
    details: responseData?.details || responseData?.error,
    originalError: error,
  };
};

/**
 * 统一的错误处理函数
 * @param error - 错误对象
 * @param customMessage - 自定义错误消息（可选）
 * @param showNotification - 是否显示通知（默认 true）
 * @returns 标准化的错误对象
 */
export const handleApiError = (
  error: unknown,
  customMessage?: string,
  showNotification = true,
  handleUnauthorizedSideEffect = true,
): ApiError => {
  let apiError: ApiError;

  // 解析错误
  if (isAxiosError(error)) {
    apiError = parseAxiosError(error);
  } else if (error instanceof Error) {
    apiError = {
      type: ErrorType.UNKNOWN_ERROR,
      message: error.message || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR],
      originalError: error,
    };
  } else {
    apiError = {
      type: ErrorType.UNKNOWN_ERROR,
      message: ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR],
      originalError: error,
    };
  }

  // 使用自定义消息（如果提供）
  if (customMessage) {
    apiError.message = customMessage;
  }

  // 处理特殊错误类型
  if (apiError.type === ErrorType.UNAUTHORIZED && handleUnauthorizedSideEffect) {
    handleUnauthorized();
  }

  // 显示错误通知
  if (showNotification) {
    showErrorNotification(apiError);
  }

  // 打印详细错误日志（开发环境）
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', {
      type: apiError.type,
      statusCode: apiError.statusCode,
      message: apiError.message,
      details: apiError.details,
      originalError: apiError.originalError,
    });
  }

  return apiError;
};

/**
 * 显示错误通知
 */
export const showErrorNotification = (error: ApiError) => {
  const duration = error.type === ErrorType.UNAUTHORIZED ? 2 : 4;

  message.error({
    content: error.message,
    duration,
    key: error.type, // 使用 type 作为 key，防止相同类型的错误重复显示
  });
};

/**
 * 处理未授权错误（401）
 */
export const handleUnauthorized = () => {
  // 清除认证信息
  Cookies.remove('accessToken');
  Cookies.remove('userId');
  localStorage.removeItem('userInfo');

  // 保存当前路径，登录后重定向
  const currentPath = window.location.pathname;
  if (currentPath !== '/login' && currentPath !== '/register') {
    sessionStorage.setItem('redirectPath', currentPath);
  }

  // 延迟跳转，给用户时间看到错误消息
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

/**
 * 创建业务错误（用于手动抛出业务逻辑错误）
 */
export const createBusinessError = (message: string, details?: unknown): ApiError => {
  return {
    type: ErrorType.VALIDATION_ERROR,
    message,
    details,
  };
};

/**
 * 检查错误是否为特定类型
 */
export const isErrorType = (error: ApiError, type: ErrorType): boolean => {
  return error.type === type;
};

/**
 * 从错误中提取用户友好的消息
 */
export const getErrorMessage = (error: unknown, fallbackMessage?: string): string => {
  if (error && typeof error === 'object' && 'message' in error && error.message) {
    return String((error as { message: unknown }).message);
  }

  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { data?: { message?: unknown } } }).response?.data?.message
  ) {
    return String(
      (error as { response?: { data?: { message?: unknown } } }).response?.data?.message,
    );
  }
  return fallbackMessage || ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];
};
